import type { CSSObject } from '@emotion/react'
import { Typography, Color, Theme } from '@opallabs/gem'

export const rootSelector = 'lapidary-menu-option'

export const selectors = {
  root: rootSelector,
  menuOptionText: `${rootSelector}__text`,
  menuOptionTrailingText: `${rootSelector}__trailing-text`,
} as const

type MenuOptionStyle = {
  disabled?: boolean
  highlightedByKeyboard?: boolean
  selected?: boolean
  theme: Theme
}

const getHoverBackgroundColor = ({
  selected,
  disabled,
  theme,
}: MenuOptionStyle): string => {
  if (disabled && selected) return Color[theme].Translucent.A16
  if (disabled) return Color[theme].Surface.N000
  if (selected) return Color[theme].Translucent.A16

  return Color[theme].Translucent.A08
}

const getBackgroundColor = ({ selected, theme }: MenuOptionStyle) =>
  selected ? Color[theme].Translucent.A16 : Color[theme].Surface.N000

export const menuOption = ({
  selected,
  disabled,
  theme,
  highlightedByKeyboard,
}: MenuOptionStyle): CSSObject => ({
  ...Typography.T500,
  alignItems: 'center',
  border: 'solid 1px',
  borderRadius: 2,
  boxSizing: 'border-box',
  display: 'flex',
  gap: '0 8px',
  lineHeight: '20px',
  listStyle: 'none',
  opacity: disabled ? 0.5 : 1,
  padding: '6px 8px',
  backgroundColor: highlightedByKeyboard
    ? getHoverBackgroundColor({ selected, disabled, theme })
    : getBackgroundColor({ selected, theme }),
  // The design draws no stroke round a row. A chosen row has the same face
  // whether or not the keyboard is on it, though, so there the stroke is the
  // only sign of focus. Elsewhere the border is clear rather than the face's
  // colour, because the face is translucent and paints under the border too,
  // and two coats of it read as a rim.
  borderColor:
    highlightedByKeyboard && selected
      ? Color[theme].Surface.A200
      : 'transparent',
  color: Color[theme].Text.Default,

  '&:hover': {
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: getHoverBackgroundColor({ selected, disabled, theme }),
  },
})

export const menuText: CSSObject = {
  flexGrow: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  [`&:has(+ .${selectors.menuOptionTrailingText})`]: {
    flexGrow: 0,
    flexShrink: 0,
    maxInlineSize: '24ch',
  },
}

export const trailingImage: CSSObject = {
  boxSizing: 'border-box',
  height: 20,
  padding: 2,
  width: 16,
}

export const checkbox: CSSObject = {
  // Override disabled style, parent is interactive
  'input:disabled': {
    opacity: 1,
    pointerEvents: 'none',
  },
}
